/**
 * Deposit booking controller.
 *
 * Retreat variants carry a `custom.full_retreat_price` money metafield. Shopify
 * charges the variant price (the deposit); the metafield holds the full retreat
 * price for that room and occupancy. This keeps the PDP panel, the add-to-cart
 * label and the line-item properties in step with the selected variant.
 *
 * The pure helpers are published on the global namespace so they can be
 * exercised without a DOM.
 */
;(function (global) {
	'use strict'

	var STRINGS = {
		fullPriceLabel: 'Full retreat price per person',
		depositLabel: 'Deposit due today',
		bookingType: 'Deposit booking',
		notePerPerson: ' deposit per person.',
		noteBalance:
			' We’ll contact you after booking to arrange the remaining balance by payment plan or bank transfer.',
	}

	var NON_DEPOSIT_STATE = {
		isDeposit: false,
		fullPriceMoney: '',
		depositMoney: '',
		depositMoneyShort: '',
		balanceMoney: '',
		note: '',
		ctaLabel: null,
		properties: {},
	}

	/**
	 * @param {Object|null} data payload rendered by snippets/deposit-booking.liquid
	 * @param {String|Number} variantId currently selected variant
	 */
	function computeDepositState(data, variantId) {
		var variants = data && data.variants
		var entry = variants ? variants[String(variantId)] : null

		if (!entry || !entry.is_deposit) return NON_DEPOSIT_STATE

		var short = entry.deposit_money_short || entry.deposit_money

		return {
			isDeposit: true,
			fullPriceMoney: entry.full_price_money,
			depositMoney: entry.deposit_money,
			depositMoneyShort: short,
			balanceMoney: entry.balance_money,
			note: short + STRINGS.notePerPerson + STRINGS.noteBalance,
			ctaLabel: 'Reserve with ' + short + ' deposit',
			properties: {
				booking_type: STRINGS.bookingType,
				full_price: entry.full_price_money,
				deposit: entry.deposit_money,
				balance: entry.balance_money,
			},
		}
	}

	/**
	 * Writes a state onto the panel and the hidden line-item property inputs.
	 * Every ref is optional so a partially re-rendered section cannot throw.
	 */
	function applyState(refs, state) {
		refs = refs || {}

		if (refs.panel) refs.panel.hidden = !state.isDeposit
		if (refs.priceWrapper && refs.priceWrapper.classList) {
			refs.priceWrapper.classList.toggle('price-wrapper--deposit', state.isDeposit)
		}

		if (state.isDeposit) {
			if (refs.fullPrice) refs.fullPrice.textContent = state.fullPriceMoney
			if (refs.deposit) refs.deposit.textContent = state.depositMoney
			if (refs.note) refs.note.textContent = state.note
		}

		var properties = refs.properties || []
		for (var i = 0; i < properties.length; i++) {
			var input = properties[i]
			var key = input.getAttribute('data-deposit-property')
			if (state.isDeposit && Object.prototype.hasOwnProperty.call(state.properties, key)) {
				input.value = state.properties[key]
				input.disabled = false
			} else {
				input.disabled = true
			}
		}

		return state
	}

	/**
	 * @returns {String|null} label to write, or null to leave the theme's own
	 * label alone (sold out / unavailable states).
	 */
	function resolveCtaLabel(state, options) {
		options = options || {}
		if (options.disabled) return null
		if (!state.isDeposit) return options.defaultLabel || null
		return state.ctaLabel
	}

	global.DepositBooking = {
		STRINGS: STRINGS,
		computeDepositState: computeDepositState,
		applyState: applyState,
		resolveCtaLabel: resolveCtaLabel,
	}

	if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return

	var controllers = {}

	function DepositBookingController(sectionId) {
		this.sectionId = sectionId
		this.data = null
		this.observer = null
		this.onFormChange = this.onFormChange.bind(this)
		document.addEventListener('change', this.onFormChange, true)
		this.refresh()
	}

	DepositBookingController.prototype.query = function (selector) {
		return document.querySelector(selector + '[data-section-id="' + this.sectionId + '"]')
	}

	DepositBookingController.prototype.readData = function () {
		var script = this.query('script[data-deposit-booking-data]')
		if (!script) return this.data
		try {
			this.data = JSON.parse(script.textContent)
		} catch (error) {
			console.error('deposit-booking: invalid payload', error)
		}
		return this.data
	}

	DepositBookingController.prototype.collectRefs = function () {
		var panel = this.query('[data-deposit-booking]')
		var lineItems = this.query('[data-deposit-line-items]')
		return {
			panel: panel,
			priceWrapper: document.getElementById('price-' + this.sectionId),
			fullPrice: panel && panel.querySelector('[data-deposit-full-price]'),
			deposit: panel && panel.querySelector('[data-deposit-amount]'),
			note: panel && panel.querySelector('[data-deposit-note]'),
			properties: lineItems
				? Array.prototype.slice.call(lineItems.querySelectorAll('[data-deposit-property]'))
				: [],
		}
	}

	DepositBookingController.prototype.form = function () {
		return document.getElementById('product-form-' + this.sectionId)
	}

	DepositBookingController.prototype.selectedVariantId = function () {
		var form = this.form()
		var input = form && form.querySelector('input[name="id"], select[name="id"]')
		return input ? input.value : null
	}

	DepositBookingController.prototype.onFormChange = function (event) {
		var target = event.target
		if (!target || target.name !== 'id') return
		var form = this.form()
		if (!form || !form.contains(target)) return
		this.refresh()
	}

	DepositBookingController.prototype.refresh = function () {
		var data = this.readData()
		if (!data) return
		var state = computeDepositState(data, this.selectedVariantId())
		this.state = state
		applyState(this.collectRefs(), state)
		this.applyCta()
		this.watchCta()
	}

	DepositBookingController.prototype.addButton = function () {
		var form = this.form()
		return form ? form.querySelector('[name="add"]') : null
	}

	DepositBookingController.prototype.applyCta = function () {
		var button = this.addButton()
		if (!button || !this.state) return
		var label = button.querySelector('span')
		if (!label) return

		var defaultLabel = (global.variantStrings && global.variantStrings.addToCart) || label.textContent
		var next = resolveCtaLabel(this.state, {
			disabled: button.hasAttribute('disabled'),
			defaultLabel: defaultLabel,
		})

		// Guarded so the mutation observer below cannot loop.
		if (next !== null && label.textContent !== next) label.textContent = next
	}

	// The theme rewrites the add-to-cart label after its own variant fetch
	// resolves, so the deposit label is re-asserted whenever that happens.
	DepositBookingController.prototype.watchCta = function () {
		var button = this.addButton()
		if (!button || button === this.observedButton) return

		if (this.observer) this.observer.disconnect()
		this.observedButton = button
		this.observer = new MutationObserver(this.applyCta.bind(this))
		this.observer.observe(button, {
			childList: true,
			subtree: true,
			characterData: true,
			attributes: true,
			attributeFilter: ['disabled'],
		})
	}

	function init(root) {
		var scope = root && root.querySelectorAll ? root : document
		var payloads = scope.querySelectorAll('[data-deposit-booking-data]')
		Array.prototype.forEach.call(payloads, function (script) {
			var sectionId = script.getAttribute('data-section-id')
			if (!sectionId) return
			if (controllers[sectionId]) controllers[sectionId].refresh()
			else controllers[sectionId] = new DepositBookingController(sectionId)
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			init(document)
		})
	} else {
		init(document)
	}

	document.addEventListener('shopify:section:load', function (event) {
		init(event.target)
	})
})(typeof window !== 'undefined' ? window : globalThis)
